import React, { useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Button } from '@/components/ui/Button';

interface DebugResult {
  offerId?: string;
  offerFound: boolean;
  kyleeInRecipients: boolean;
  storedUserId?: string;
  actualUserId?: string;
  userExists: boolean;
  idsMatch: boolean;
  inRecipientUserIds: boolean;
  wouldAppearInDashboard: boolean;
  details: string[];
  errors: string[];
}

export default function DebugOffer() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<DebugResult | null>(null);

  const debugOffer = async () => {
    setLoading(true);
    const details: string[] = [];
    const errors: string[] = [];
    const debugResult: DebugResult = {
      offerFound: false,
      kyleeInRecipients: false,
      userExists: false,
      idsMatch: false,
      inRecipientUserIds: false,
      wouldAppearInDashboard: false,
      details,
      errors,
    };

    try {
      // 1. Find the specific route offer
      details.push('Searching for route: "123 yellow street → 123 blue street"');
      const offersSnapshot = await getDocs(collection(db, 'routeOffers'));

      const targetOffer = offersSnapshot.docs.find(doc => {
        const data = doc.data();
        const pickup = (data.pickupLocation || '').toLowerCase();
        const dropoff = (data.dropoffLocation || '').toLowerCase();
        return pickup.includes('123 yellow') && dropoff.includes('123 blue');
      });

      if (!targetOffer) {
        errors.push('Route offer not found');
        setResult(debugResult);
        setLoading(false);
        return;
      }

      debugResult.offerFound = true;
      debugResult.offerId = targetOffer.id;
      details.push(`Found offer: ${targetOffer.id}`);

      const offerData = targetOffer.data();

      // 2. Check recipients
      details.push('Checking recipients in the offer...');
      const kyleeRecipient = offerData.recipients?.find((r: any) =>
        r.email?.toLowerCase().includes('kylee@shuttlebees.com') ||
        r.userEmail?.toLowerCase().includes('kylee@shuttlebees.com')
      );

      if (!kyleeRecipient) {
        errors.push('kylee@shuttlebees.com not found in recipients array');
      } else {
        debugResult.kyleeInRecipients = true;
        debugResult.storedUserId = kyleeRecipient.userId;
        details.push(`Found Kylee in recipients. Stored userId: ${kyleeRecipient.userId}`);
      }

      // 3. Look up kylee@shuttlebees.com in users collection
      details.push('Looking up kylee@shuttlebees.com in users collection...');
      const usersQuery = query(
        collection(db, 'users'),
        where('email', '==', 'kylee@shuttlebees.com')
      );
      const usersSnapshot = await getDocs(usersQuery);

      if (usersSnapshot.empty) {
        errors.push('kylee@shuttlebees.com does NOT exist in users collection - user needs to sign up first!');
      } else {
        debugResult.userExists = true;
        const kyleeUser = usersSnapshot.docs[0];
        debugResult.actualUserId = kyleeUser.id;
        const userData = kyleeUser.data();
        details.push(`Found kylee@shuttlebees.com in users collection`);
        details.push(`Firebase UID: ${kyleeUser.id}`);
        details.push(`Email: ${userData.email}`);
        details.push(`Name: ${userData.displayName || userData.name || 'N/A'}`);

        // 4. Compare stored userId vs actual UID
        if (kyleeRecipient) {
          debugResult.idsMatch = kyleeRecipient.userId === kyleeUser.id;
          if (debugResult.idsMatch) {
            details.push('✅ UserIds MATCH - should work correctly');
          } else {
            errors.push(`UserIds DO NOT MATCH!`);
            errors.push(`  Expected: ${kyleeUser.id}`);
            errors.push(`  Got: ${kyleeRecipient.userId}`);
          }
        }

        // 5. Check recipientUserIds array
        debugResult.inRecipientUserIds = offerData.recipientUserIds?.includes(kyleeUser.id);
        if (debugResult.inRecipientUserIds) {
          details.push('✅ recipientUserIds includes correct UID');
        } else {
          errors.push('recipientUserIds does NOT include correct UID');
          errors.push(`  Array contains: ${JSON.stringify(offerData.recipientUserIds)}`);
          errors.push(`  Should include: ${kyleeUser.id}`);
        }

        // 6. Test the dashboard query
        details.push('Testing dashboard query...');
        const dashboardQuery = query(
          collection(db, 'routeOffers'),
          where('recipientUserIds', 'array-contains', kyleeUser.id)
        );
        const dashboardResults = await getDocs(dashboardQuery);
        debugResult.wouldAppearInDashboard = dashboardResults.docs.some(doc => doc.id === targetOffer.id);

        if (debugResult.wouldAppearInDashboard) {
          details.push('✅ This offer WOULD appear in dashboard query');
        } else {
          errors.push('This offer WOULD NOT appear in dashboard query');
        }
      }

      setResult(debugResult);
    } catch (error: any) {
      errors.push(`Error during debug: ${error.message}`);
      setResult(debugResult);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Debug Route Offer</h1>

      <div className="mb-6">
        <p className="text-gray-600 mb-4">
          This tool will help diagnose why the route offer "123 yellow street → 123 blue street"
          is not appearing in kylee@shuttlebees.com's dashboard.
        </p>
        <Button onClick={debugOffer} disabled={loading}>
          {loading ? 'Debugging...' : 'Run Debug'}
        </Button>
      </div>

      {result && (
        <div className="space-y-4">
          {/* Summary */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h2 className="text-lg font-semibold mb-3">Summary</h2>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span className={result.offerFound ? 'text-green-600' : 'text-red-600'}>
                  {result.offerFound ? '✅' : '❌'}
                </span>
                <span>Offer Found: {result.offerFound ? 'Yes' : 'No'}</span>
              </div>
              {result.offerId && (
                <div className="ml-6 text-gray-600">Offer ID: {result.offerId}</div>
              )}

              <div className="flex items-center gap-2">
                <span className={result.userExists ? 'text-green-600' : 'text-red-600'}>
                  {result.userExists ? '✅' : '❌'}
                </span>
                <span>User Exists: {result.userExists ? 'Yes' : 'No'}</span>
              </div>
              {result.actualUserId && (
                <div className="ml-6 text-gray-600">Firebase UID: {result.actualUserId}</div>
              )}

              <div className="flex items-center gap-2">
                <span className={result.kyleeInRecipients ? 'text-green-600' : 'text-red-600'}>
                  {result.kyleeInRecipients ? '✅' : '❌'}
                </span>
                <span>In Recipients List: {result.kyleeInRecipients ? 'Yes' : 'No'}</span>
              </div>
              {result.storedUserId && (
                <div className="ml-6 text-gray-600">Stored userId: {result.storedUserId}</div>
              )}

              <div className="flex items-center gap-2">
                <span className={result.idsMatch ? 'text-green-600' : 'text-red-600'}>
                  {result.idsMatch ? '✅' : '❌'}
                </span>
                <span>UserIds Match: {result.idsMatch ? 'Yes' : 'No'}</span>
              </div>

              <div className="flex items-center gap-2">
                <span className={result.wouldAppearInDashboard ? 'text-green-600' : 'text-red-600'}>
                  {result.wouldAppearInDashboard ? '✅' : '❌'}
                </span>
                <span>Would Appear in Dashboard: {result.wouldAppearInDashboard ? 'Yes' : 'No'}</span>
              </div>
            </div>
          </div>

          {/* Errors */}
          {result.errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h2 className="text-lg font-semibold text-red-900 mb-3">Problems Found</h2>
              <ul className="space-y-1 text-sm text-red-800 font-mono">
                {result.errors.map((error, i) => (
                  <li key={i}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Details */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h2 className="text-lg font-semibold mb-3">Details</h2>
            <ul className="space-y-1 text-sm text-gray-700 font-mono">
              {result.details.map((detail, i) => (
                <li key={i}>{detail}</li>
              ))}
            </ul>
          </div>

          {/* Solution */}
          {!result.wouldAppearInDashboard && result.userExists && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h2 className="text-lg font-semibold text-blue-900 mb-3">Solution</h2>
              <div className="text-sm text-blue-800 space-y-2">
                {!result.idsMatch && (
                  <p>
                    The offer has an incorrect userId stored (likely the email string instead of the Firebase UID).
                    You need to either:
                  </p>
                )}
                <ol className="list-decimal ml-5 space-y-1">
                  <li>Delete this offer and create a new one (the system will now properly look up the UID)</li>
                  <li>Or manually update the Firestore document to use the correct UID: {result.actualUserId}</li>
                </ol>
              </div>
            </div>
          )}
          {!result.userExists && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h2 className="text-lg font-semibold text-yellow-900 mb-3">Solution</h2>
              <p className="text-sm text-yellow-800">
                kylee@shuttlebees.com needs to sign up and create an account before offers can be created for them.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
